import { useCallback, useEffect, useRef, useState } from "react";
import { AlarmController } from "./AlarmController";
import { computeAlarmState, type AlarmState } from "./alarmState";
import type { TerminalOrder } from "../lib/types";

// Bindet den zentralen AlarmController an die aus Serverdaten abgeleitete
// Menge offener awaiting_merchant-Orders. Ein Controller, kein Loop je Order.
export function useAlarm() {
  const controller = useRef<AlarmController | null>(null);
  const prevOpen = useRef<Set<string>>(new Set());
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [alarm, setAlarm] = useState<AlarmState>({ active: false, openCount: 0, openOrderIds: [], shouldPulse: false });

  if (controller.current === null) controller.current = new AlarmController();

  const enableAudio = useCallback(async () => {
    const ok = await controller.current!.enable();
    setAudioEnabled(ok);
    // Bereits offene Orders sofort hörbar machen.
    controller.current!.setActive(ok && alarm.active);
  }, [alarm.active]);

  // Aus aktuellen Serverdaten neu berechnen (optional Reminder-order_id).
  const recompute = useCallback((orders: TerminalOrder[], reminderOrderId?: string) => {
    const reminders = reminderOrderId ? new Set([reminderOrderId]) : new Set<string>();
    const next = computeAlarmState(orders, prevOpen.current, reminders);
    prevOpen.current = new Set(next.openOrderIds);
    setAlarm(next);
    const ctl = controller.current!;
    ctl.setActive(next.active);
    if (next.shouldPulse) ctl.pulse();
  }, []);

  useEffect(() => {
    return () => { controller.current?.dispose(); controller.current = null; };
  }, []);

  const stopAndDispose = useCallback(() => {
    controller.current?.setActive(false);
    controller.current?.dispose();
    controller.current = new AlarmController();
    prevOpen.current = new Set();
    setAudioEnabled(false);
    setAlarm({ active: false, openCount: 0, openOrderIds: [], shouldPulse: false });
  }, []);

  return { alarm, audioEnabled, enableAudio, recompute, stopAndDispose };
}
